import React, { useState } from 'react';
import { Plus, Trash2, Eye, EyeOff, AlertCircle, Loader } from 'lucide-react';
import {
  useSetupConfig,
  useAddCustomValue,
  useDeleteCustomValue,
  useUpdateConfig,
  useReactivateCustomValue,
  usePermanentlyDeleteCustomValue,
  useConfigVersionPolling,
  type ConfigType,
  type CustomValue,
} from '../hooks/useSetupConfigs';

type ConfigTypeKey = ConfigType;

interface ConfigValue {
  id: string;
  label: string;
  value: string;
  enabled: boolean;
}

const CONFIG_TYPES: { key: ConfigTypeKey; label: string; description: string }[] = [
  { key: 'pipe_material', label: 'Pipe Material', description: 'Materials used in pipes' },
  { key: 'reporting_type', label: 'Reporting Type', description: 'How the issue was reported' },
  { key: 'pipe_diameter', label: 'Pipe Diameter', description: 'Pipe diameter sizes' },
  { key: 'technical_allocation', label: 'Technical Allocation', description: 'Response time allocation' },
  { key: 'damage_type', label: 'Damage Type', description: 'Types of damage to pipes' },
  { key: 'repair_material', label: 'Repair Material', description: 'Materials for repairs' },
  { key: 'zones', label: 'Zones', description: 'Service zones or areas' },
  { key: 'supply_pressure', label: 'Supply Pressure', description: 'Water supply pressure levels' },
  { key: 'hole_size', label: 'Hole Size', description: 'Hole sizes for water loss calculation' },
];

export default function SettingsRequests() {
  const [selectedConfig, setSelectedConfig] = useState<ConfigTypeKey>('pipe_material');
  const [successMessage, setSuccessMessage] = useState('');
  const [newValueLabel, setNewValueLabel] = useState('');
  const [newValueError, setNewValueError] = useState('');

  // Use React Query hooks for data management
  const { data: configResponse, isLoading, error } = useSetupConfig(selectedConfig);
  const addCustomValueMutation = useAddCustomValue(selectedConfig);
  const deleteCustomValueMutation = useDeleteCustomValue(selectedConfig);
  const reactivateCustomValueMutation = useReactivateCustomValue(selectedConfig);
  const permanentlyDeleteMutation = usePermanentlyDeleteCustomValue(selectedConfig);
  const updateConfigMutation = useUpdateConfig(selectedConfig);

  // Enable polling for multi-user cache sync (5 minutes)
  useConfigVersionPolling(selectedConfig, { enabled: true, interval: 1000 * 60 * 5 });

  // Extract values from response
  const config = configResponse?.config;
  const useDefaults = config?.useDefaults ?? true;
  const customValues = config?.customValues ?? [];
  const defaultValues = config?.defaultValues ?? [];

  // Computed values
  const enabledDefaults = defaultValues || [];
  const enabledCustom = customValues.filter((cv) => cv.enabled);
  const disabledCustom = customValues.filter((cv) => !cv.enabled);

  // Handlers
  const handleAddCustomValue = async () => {
    if (!newValueLabel.trim()) {
      setNewValueError('Value label is required');
      return;
    }

    newValueError && setNewValueError('');
    try {
      await addCustomValueMutation.mutateAsync({
        label: newValueLabel,
        value: newValueLabel.toLowerCase().replace(/\s+/g, '_'),
      });
      setNewValueLabel('');
      setSuccessMessage('Custom value added successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setNewValueError(err.message || 'Failed to add custom value');
    }
  };

  const handleDeleteCustomValue = async (valueId: string) => {
    try {
      await deleteCustomValueMutation.mutateAsync(valueId);
      setSuccessMessage('Custom value deactivated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      // Error handling is automatic with React Query
    }
  };

  const handleReactivateCustomValue = async (valueId: string) => {
    try {
      await reactivateCustomValueMutation.mutateAsync(valueId);
      setSuccessMessage('Custom value reactivated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      // Error handling is automatic with React Query
    }
  };

  const handlePermanentlyDeleteCustomValue = async (valueId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this value? This cannot be undone.')) {
      return;
    }

    try {
      await permanentlyDeleteMutation.mutateAsync(valueId);
      setSuccessMessage('Custom value permanently deleted');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      // Error handling is automatic
    }
  };

  const handleToggleDefaults = async () => {
    try {
      await updateConfigMutation.mutateAsync({
        useDefaults: !useDefaults,
        customValues,
      });
      setSuccessMessage('Configuration updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      // Error handling is automatic
    }
  };

  const selectedConfigData = CONFIG_TYPES.find((ct) => ct.key === selectedConfig);
  const isSaving =
    addCustomValueMutation.isPending ||
    deleteCustomValueMutation.isPending ||
    reactivateCustomValueMutation.isPending ||
    permanentlyDeleteMutation.isPending ||
    updateConfigMutation.isPending;

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl">
        <div className="text-center py-12">
          <p className="text-gray-500">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
      <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Setup</h1>
        <p className="text-gray-600">Manage dropdown values for service requests</p>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error instanceof Error ? error.message : 'Failed to load configuration'}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-green-700 flex-shrink-0 mt-2" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Config Type Tabs */}
      <div className="mb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {CONFIG_TYPES.map((ct) => (
            <button
              key={ct.key}
              onClick={() => setSelectedConfig(ct.key)}
              className={`px-4 py-3 font-medium text-sm rounded-lg transition-colors border-2 ${
                selectedConfig === ct.key
                  ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {ct.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      {selectedConfigData && (
        <div className="space-y-8">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">{selectedConfigData.label}</h2>
            <p className="text-gray-600 text-sm">{selectedConfigData.description}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Default Values */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {/* Default Values Section */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Default Values</h3>
                    <button
                      onClick={handleToggleDefaults}
                      disabled={isSaving}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all ${
                        useDefaults
                          ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {useDefaults ? (
                        <>
                          <Eye className="w-4 h-4" /> Enabled
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4" /> Disabled
                        </>
                      )}
                    </button>
                  </div>

                  <div
                    className={`space-y-2 ${useDefaults ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}
                  >
                    {enabledDefaults.length > 0 ? (
                      enabledDefaults.map((value) => (
                        <div
                          key={value.id}
                          className="flex items-center justify-between bg-white px-4 py-3 rounded border border-gray-200"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{value.label}</p>
                            <p className="text-xs text-gray-500">{value.value}</p>
                          </div>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">System</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm py-4">No default values available</p>
                    )}
                  </div>
                </div>

                {/* Custom Values Section */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Custom Values</h3>

                  {/* Add New Custom Value */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Custom Value
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newValueLabel}
                        onChange={(e) => {
                          setNewValueLabel(e.target.value);
                          setNewValueError('');
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !isSaving) {
                            handleAddCustomValue();
                          }
                        }}
                        placeholder="Enter value label"
                        disabled={isSaving}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <button
                        onClick={handleAddCustomValue}
                        disabled={!newValueLabel.trim() || isSaving}
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>
                    {newValueError && <p className="text-red-600 text-sm mt-2">{newValueError}</p>}
                  </div>

                  {/* Enabled Custom Values */}
                  {enabledCustom.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-3">Active</p>
                      <div className="space-y-2">
                        {enabledCustom.map((value) => (
                          <div
                            key={value.id}
                            className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded border border-gray-200"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{value.label}</p>
                              <p className="text-xs text-gray-500">{value.value}</p>
                            </div>
                            <button
                              onClick={() => handleDeleteCustomValue(value.id)}
                              disabled={isSaving}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Disabled Custom Values */}
                  {disabledCustom.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-3">Inactive</p>
                      <div className="space-y-2">
                        {disabledCustom.map((value) => (
                          <div
                            key={value.id}
                            className="flex items-center justify-between bg-gray-100 px-4 py-3 rounded border border-gray-300"
                          >
                            <div>
                              <p className="font-medium text-gray-700">{value.label}</p>
                              <p className="text-xs text-gray-500">{value.value}</p>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleReactivateCustomValue(value.id)}
                                disabled={reactivateCustomValueMutation.isPending || permanentlyDeleteMutation.isPending}
                                className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Reactivate this value"
                              >
                                {reactivateCustomValueMutation.isPending ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => handlePermanentlyDeleteCustomValue(value.id)}
                                disabled={permanentlyDeleteMutation.isPending || reactivateCustomValueMutation.isPending}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Permanently delete this value"
                              >
                                {permanentlyDeleteMutation.isPending ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {enabledCustom.length === 0 && disabledCustom.length === 0 && (
                    <p className="text-gray-500 text-sm py-4">No custom values yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-4">Preview</h3>
                <p className="text-xs text-gray-600 mb-4">
                  This is how the dropdown will appear in service requests:
                </p>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    {selectedConfigData.label}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">-- Select {selectedConfigData.label.toLowerCase()} --</option>
                    {useDefaults && enabledDefaults.map((v) => (
                      <option key={v.id} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                    {enabledCustom.map((v) => (
                      <option key={v.id} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>

                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <p className="text-xs text-gray-600 mb-3">
                      <strong>Total values:</strong> {(useDefaults ? enabledDefaults.length : 0) + enabledCustom.length}
                    </p>
                    {useDefaults && (
                      <p className="text-xs text-gray-600 mb-2">
                        • {enabledDefaults.length} system defaults
                      </p>
                    )}
                    {enabledCustom.length > 0 && (
                      <p className="text-xs text-gray-600">
                        • {enabledCustom.length} custom values
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
